import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { getSubCompanyId, getTenantId } from '../utils/tokenManager';
import {
  resolveVioAssetUrl,
  pickVoucherSchemaImageRaw,
  pickVoucherSchemaDisplayName,
  pickTokenLogoRaw
} from '../utils/vioAssetUrl';
import {
  ReceiptScopeTab,
  ReceiptQuantityTab,
  AmountThresholdsTab,
  ReviewModeTab,
  RewardsTab,
  UserLimitsTab,
  TimeRulesTab
} from '../components/rules';
import '../components/rules/Rules.css';
import './RulesConfig.css';


/** VIO token list: use effectiveCompanyBalance (sendable) when present, else companyBalance / balance. */
function pickTokenListBalance(item) {
  return item.effectiveCompanyBalance ?? item.companyBalance ?? item.balance;
}

function RulesConfig() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('scope'); // Start with Receipt Scope
  const [config, setConfig] = useState(null);
  const [voucherSchemas, setVoucherSchemas] = useState([]);
  const [tokenList, setTokenList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [wizardError, setWizardError] = useState(null);

  const scopeTabRef = useRef(null);
  const quantityTabRef = useRef(null);
  const thresholdsTabRef = useRef(null);
  const reviewTabRef = useRef(null);
  const rewardsTabRef = useRef(null);
  const limitsTabRef = useRef(null);
  const timeTabRef = useRef(null);

  // Fetch campaign configuration
  const fetchConfig = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/logic/config');
      if (response.data.success) {
        setConfig(response.data.data);
      }
    } catch (err) {
      setError('Failed to fetch configuration');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch voucher schemas for rewards tab — VIO MiniApp: campaign voucherSchema published list only
  const fetchVoucherSchemas = useCallback(async () => {
    try {
      const response = await api.get('/vio/vouchers', { params: { page: 1, size: 200 } });
      if (response.data?.status !== 'Success') return;
      const items = response.data?.message?.data || [];
      const schemas = items.map((item) => {
        const s = item.voucherSchema || {};
        const campaignQuantity = item.campaignQuantity ?? (s.isLimited ? (s.quantity ?? 0) : -1);
        const campaignClaimed = item.campaignClaimedQuantity ?? (item.isMaxQuantity ? Math.max(0, campaignQuantity) : 0);
        const campaignRemaining = item.campaignRemainingQuantity ?? (
          campaignQuantity > 0 ? Math.max(0, campaignQuantity - campaignClaimed) : campaignQuantity
        );
        const unlimited = campaignRemaining < 0 || campaignQuantity < 0 || s.isLimited === false;
        const rawImage = pickVoucherSchemaImageRaw(s, item);
        const displayName = pickVoucherSchemaDisplayName(s);
        return {
          id: s.voucherSchemaId || item.campaignVoucherSchemaId,
          schemaId: s.voucherSchemaId || item.campaignVoucherSchemaId,
          campaignVoucherSchemaId: item.campaignVoucherSchemaId || s.voucherSchemaId || '',
          campaignId: item.campaignId || '',
          // Campaign / schema active flag — inactive campaigns cannot be issued as rewards
          isActive: item.isActive !== false && s.isActive !== false,
          name: displayName,
          imageUrl: resolveVioAssetUrl(rawImage) || undefined,
          value: s.value,
          isLimited: !unlimited,
          totalQuantity: unlimited ? -1 : campaignQuantity,
          claimedQuantity: unlimited ? 0 : campaignClaimed,
          remainingQuantity: unlimited ? -1 : campaignRemaining,
          // VIO may use expiryEndDatetime, endDate, or nested campaign dates — normalize for expiry checks in RewardsTab
          expiryEnd:
            s.expiryEndDatetime ||
            s.expiryEndDate ||
            s.endDate ||
            s.expiryDate ||
            item.campaignEndDate ||
            item.endDate,
          maxClaimsPerUser: s.maxPurchasePerUser ?? 1,
        };
      });
      setVoucherSchemas(schemas);
    } catch (err) {
      console.error('Failed to fetch voucher schemas:', err);
    }
  }, []);

  // Fetch token list for rewards tab
  // Prefer External API (no access token needed) when tenantId exists; fallback to MiniApp API
  const fetchTokenList = useCallback(async () => {
    try {
      const tenantId = getTenantId();
      const subCompanyId = getSubCompanyId();
      if (tenantId) {
        try {
          const params = { tenantId };
          if (subCompanyId != null) {
            params.subCompanyId = subCompanyId;
          }
          const response = await api.get('/vio/external/tokens', { params });
          const rawData = response.data?.data ?? response.data;
          const items = Array.isArray(rawData) ? rawData : [];
          const tokens = items.map(item => {
            const rawLogo = pickTokenLogoRaw(item);
            return {
              id: item._id || item.dbTokId,
              dbTokId: item.dbTokId ?? null,
              mongoId: item._id ?? null,
              name: item.name || item.tokenName,
              symbol: item.symbol || item.tokenSymbol,
              balance: pickTokenListBalance(item),
              decimal: item.decimal,
              logoUrl: resolveVioAssetUrl(rawLogo) || null
            };
          });
          setTokenList(tokens);
          return;
        } catch (extErr) {
          console.warn('External tokens API failed, falling back to MiniApp API:', extErr);
        }
      }
      // Fallback: MiniApp API (requires access token)
      const response = await api.get('/vio/tokens', { params: { page: 1, size: 100 } });
      if (response.data?.status === 'Success' && response.data?.message?.data) {
        const tokens = response.data.message.data.map(item => {
          const rawLogo = pickTokenLogoRaw(item);
          return {
            id: item.dbTokId,
            dbTokId: item.dbTokId ?? null,
            mongoId: item._id ?? null,
            name: item.tokenName,
            symbol: item.tokenSymbol,
            balance: pickTokenListBalance(item),
            decimal: item.decimal,
            logoUrl: resolveVioAssetUrl(rawLogo) || null
          };
        });
        setTokenList(tokens);
      }
    } catch (err) {
      console.error('Failed to fetch token list:', err);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
    fetchVoucherSchemas();
    fetchTokenList();
  }, [fetchConfig, fetchVoucherSchemas, fetchTokenList]);

  // Update configuration (partial updates)
  const handleUpdateConfig = async (updates) => {
    try {
      const response = await api.put('/logic/config', updates);
      if (response.data.success) {
        setConfig(response.data.data);
      }
      return response.data;
    } catch (err) {
      console.error('Failed to update config:', err);
      throw err;
    }
  };

  // Get tab steps configuration
  const getTabSteps = () => {
    return [
      { id: 'scope', number: 1, label: 'Receipt Scope', shortLabel: 'Receipt Scope' },
      { id: 'quantity', number: 2, label: 'Quantity', shortLabel: 'Quantity' },
      { id: 'thresholds', number: 3, label: 'Thresholds', shortLabel: 'Thresholds' },
      { id: 'review', number: 4, label: 'Review Mode', shortLabel: 'Review Mode' },
      { id: 'rewards', number: 5, label: 'Rewards', shortLabel: 'Rewards' },
      { id: 'limits', number: 6, label: 'User Limits', shortLabel: 'User Limits' },
      { id: 'time', number: 7, label: 'Time Rules', shortLabel: 'Time Rules' }
    ];
  };

  // Check if a step is completed based on config
  const isStepCompleted = (stepId) => {
    if (!config) return false;
    
    switch (stepId) {
      case 'scope':
        // Check if tenants are configured
        return config.eligibleTenants && config.eligibleTenants.length > 0;
      
      case 'quantity':
        // Check if quantity rules are set
        return config.receiptQuantityRules && 
               (config.receiptQuantityRules.maxTotalReceipts !== undefined ||
                config.receiptQuantityRules.categoryLimits);
      
      case 'thresholds':
        // Check if amount thresholds are configured
        return config.amountThresholds && 
               Object.keys(config.amountThresholds).length > 0;
      
      case 'review':
        // Check if review mode is configured
        return config.reviewMode !== undefined;
      
      case 'rewards':
        // Check if rewards are configured
        return config.rewards && 
               ((config.rewards.voucherPool && config.rewards.voucherPool.length > 0) ||
                (config.rewards.tokenRules && config.rewards.tokenRules.length > 0));
      
      case 'limits':
        // Check if user limits are configured
        return config.userLimits && 
               (config.userLimits.maxClaimsPerUser !== undefined ||
                config.userLimits.maxRewardsPerUser !== undefined);
      
      case 'time':
        // Check if time rules are configured
        return config.timeRules && 
               (config.timeRules.campaignStart !== null ||
                config.timeRules.campaignEnd !== null ||
                config.timeRules.receiptDateBuffer !== undefined);
      
      default:
        return false;
    }
  };

  const getCurrentStepIndex = () => {
    return getTabSteps().findIndex(step => step.id === activeTab);
  };

  const handleNext = () => {
    setWizardError(null);
    const steps = getTabSteps();
    const currentIndex = getCurrentStepIndex();
    if (currentIndex < steps.length - 1) {
      setActiveTab(steps[currentIndex + 1].id);
    }
  };

  const handleWizardDone = () => {
    setWizardError(null);
    navigate('/submissions');
  };

  const handleBack = () => {
    setWizardError(null);
    const steps = getTabSteps();
    const currentIndex = getCurrentStepIndex();
    if (currentIndex > 0) {
      setActiveTab(steps[currentIndex - 1].id);
    }
  };

  const handleCancel = () => {
    setWizardError(null);
    navigate('/submissions');
  };

  const isFirstStep = () => {
    return getCurrentStepIndex() === 0;
  };

  const isLastStep = () => {
    return getCurrentStepIndex() === getTabSteps().length - 1;
  };

  const commonTabProps = {
    config,
    onUpdate: handleUpdateConfig
  };

  if (loading) {
    return (
      <div className="rules-config">
        <div className="loading-state">
          <div className="loading-spinner-icon"></div>
          <p>Loading configuration...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="rules-config">
      {/* Breadcrumb */}
      <div className="breadcrumb">
        <button className="breadcrumb-link" onClick={() => navigate('/submissions')}>
          Receipt Ops
        </button>
        <span className="breadcrumb-separator">&gt;</span>
        <span className="breadcrumb-current">Rules Configuration</span>
      </div>

      {/* Page Header */}
      <div className="page-header-wizard">
        <h1>Rules Configuration</h1>
        {config?.updatedAt && (
          <div className="last-updated">
            Last updated: {new Date(config.updatedAt).toLocaleString()}
          </div>
        )}
      </div>

      {/* Warning Message */}
      {config && (() => {
        const missingSteps = getTabSteps().filter(step => !isStepCompleted(step.id));
        const totalSteps = getTabSteps().length;
        const completedCount = totalSteps - missingSteps.length;
        const completeness = Math.round((completedCount / totalSteps) * 100);
        
        if (missingSteps.length > 0) {
          return (
            <div className="warning-message">
              <span className="warning-icon">⚠️</span>
              <span>
                {missingSteps.length} configuration{missingSteps.length > 1 ? 's are' : ' is'} missing: {' '}
                {missingSteps.map((step, index) => (
                  <React.Fragment key={step.id}>
                    <button 
                      className="missing-config-link"
                      onClick={() => {
                        setWizardError(null);
                        setActiveTab(step.id);
                      }}
                    >
                      {step.label}
                    </button>
                    {index < missingSteps.length - 1 && ', '}
                  </React.Fragment>
                ))}
                . Configuration completeness {completeness}%
              </span>
            </div>
          );
        }
        return null;
      })()}

      {error && <div className="error-message">{error}</div>}
      {wizardError && (
        <div className="error-message" role="alert">
          {wizardError}
        </div>
      )}

      {/* Stepper Navigation */}
      <div className="stepper-container">
        {getTabSteps().map((step, index) => {
          const currentIndex = getCurrentStepIndex();
          const isActive = index === currentIndex;
          const isCompleted = isStepCompleted(step.id);
          
          return (
            <React.Fragment key={step.id}>
              <div 
                className={`stepper-step ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}
                onClick={() => {
                  setWizardError(null);
                  setActiveTab(step.id);
                }}
              >
                <div className="stepper-label">
                  {step.number}. {step.shortLabel}
                </div>
              </div>
              {index < getTabSteps().length - 1 && (
                <div className={`stepper-line ${isCompleted ? 'completed' : ''}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Tab content: all steps stay mounted so drafts persist while navigating; each step saves via its own Save control */}
      <div className="wizard-content">
        <div className={activeTab === 'scope' ? '' : 'wizard-panel-hidden'}>
          <ReceiptScopeTab ref={scopeTabRef} {...commonTabProps} />
        </div>
        <div className={activeTab === 'quantity' ? '' : 'wizard-panel-hidden'}>
          <ReceiptQuantityTab ref={quantityTabRef} {...commonTabProps} />
        </div>
        <div className={activeTab === 'thresholds' ? '' : 'wizard-panel-hidden'}>
          <AmountThresholdsTab ref={thresholdsTabRef} {...commonTabProps} />
        </div>
        <div className={activeTab === 'review' ? '' : 'wizard-panel-hidden'}>
          <ReviewModeTab ref={reviewTabRef} {...commonTabProps} />
        </div>
        <div className={activeTab === 'rewards' ? '' : 'wizard-panel-hidden'}>
          <RewardsTab
            ref={rewardsTabRef}
            {...commonTabProps}
            voucherSchemas={voucherSchemas}
            tokenList={tokenList}
          />
        </div>
        <div className={activeTab === 'limits' ? '' : 'wizard-panel-hidden'}>
          <UserLimitsTab ref={limitsTabRef} {...commonTabProps} />
        </div>
        <div className={activeTab === 'time' ? '' : 'wizard-panel-hidden'}>
          <TimeRulesTab ref={timeTabRef} {...commonTabProps} />
        </div>
      </div>

      {/* Action Buttons */}
      <div className="wizard-actions">
        {isFirstStep() ? (
          <button className="btn-wizard btn-cancel" onClick={handleCancel}>
            Cancel
          </button>
        ) : (
          <button className="btn-wizard btn-back" onClick={handleBack}>
            Back
          </button>
        )}
        <button
          className="btn-wizard btn-next"
          onClick={isLastStep() ? handleWizardDone : handleNext}
        >
          {isLastStep() ? 'Done' : 'Next'}
        </button>
      </div>
    </div>
  );
}

export default RulesConfig;
